import { useEffect, useRef } from 'react';

/**
 * Render / lifecycle instrumentation for the sequence graph, for hunting leaks and runaway re-renders.
 *
 * OFF by default — it must never cost anything (or spam the console) in normal use. Turn it on from
 * DevTools and reload:
 *
 *     localStorage.setItem('seqGraphDebug', '1'); location.reload();
 *     localStorage.removeItem('seqGraphDebug'); location.reload();
 *
 * Then drive the graph and read the tally:
 *
 *     __seqGraph()            // live / everMounted / renders per component + JS heap
 *     __seqGraph.reset()      // zero the counters, to measure one interaction in isolation
 *
 * How to read it: `live` is the number of mounted instances RIGHT NOW. Interact, then return the UI to
 * where it started — every `live` should come back to its starting value. A `live` that only ever climbs
 * is a leak (something is holding a mounted subtree). `everMounted` climbing while `live` stays flat is
 * churn, not a leak: a subtree is being thrown away and rebuilt (usually an unstable key or prop).
 */

const STORAGE_KEY = 'seqGraphDebug';

/** Read the toggle ONCE — this is consulted on every render, so it can't touch localStorage each time. */
let enabledCache: boolean | null = null;
export function seqDebugEnabled(): boolean {
  if (enabledCache == null) {
    try {
      enabledCache = typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      enabledCache = false; // storage blocked (private mode / embedded) — stay off
    }
  }
  return enabledCache;
}

interface ComponentStat {
  /** Instances mounted right now. Should return to its baseline after an interaction is undone. */
  live: number;
  /** Instances ever mounted. Climbing while `live` is flat = re-mount churn, not a leak. */
  everMounted: number;
  /** Total renders across all instances of this component. */
  renders: number;
}

/** Chrome-only heap counters (`performance.memory`); absent in Firefox / Safari. */
interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface GraphStats {
  components: Record<string, ComponentStat>;
  /** Used JS heap in MB, or null where the browser doesn't expose it. */
  heapMB: number | null;
}

const stats = new Map<string, ComponentStat>();
let nextInstanceId = 0;

function statFor(name: string): ComponentStat {
  const existing = stats.get(name);
  if (existing != null) return existing;
  const fresh: ComponentStat = { live: 0, everMounted: 0, renders: 0 };
  stats.set(name, fresh);
  return fresh;
}

function heapMB(): number | null {
  if (typeof performance === 'undefined') return null;
  const perf: Performance & { memory?: MemoryInfo } = performance;
  const used = perf.memory?.usedJSHeapSize;
  return used == null ? null : Math.round((used / 1024 / 1024) * 10) / 10;
}

function snapshot(): GraphStats {
  const components: Record<string, ComponentStat> = {};
  for (const [name, s] of [...stats.entries()].sort()) components[name] = { ...s };
  return { components, heapMB: heapMB() };
}

declare global {
  interface Window {
    __seqGraph?: (() => GraphStats) & { reset?: () => void };
  }
}

/** Publish the console entry point the moment anything is instrumented (once, and only when enabled). */
function ensureConsoleHook(): void {
  if (typeof window === 'undefined' || window.__seqGraph != null) return;
  const read = (): GraphStats => {
    const snap = snapshot();
    console.table(snap.components);
    console.log(`[seq] heap: ${snap.heapMB == null ? 'n/a (Chrome only)' : `${snap.heapMB} MB`}`);
    return snap;
  };
  read.reset = (): void => {
    for (const s of stats.values()) {
      s.everMounted = s.live; // keep the live count — only the cumulative counters reset
      s.renders = 0;
    }
    console.log('[seq] counters reset');
  };
  window.__seqGraph = read;
}

/**
 * Log this component's renders and mount/unmount, and fold it into the {@link snapshot} tally.
 *
 * Call it unconditionally at the top of the component — it no-ops internally when the toggle is off, so
 * the hook count never changes. `detail` is a THUNK so whatever it reports (array lengths, ids) is only
 * computed when debugging is on.
 */
export function useGraphRender(name: string, detail?: () => Record<string, unknown>): void {
  const idRef = useRef<number | null>(null);
  const rendersRef = useRef(0);
  const on = seqDebugEnabled();

  if (on) {
    idRef.current ??= ++nextInstanceId;
    rendersRef.current++;
    statFor(name).renders++;
    ensureConsoleHook();
    // NEVER let instrumentation take down the thing it instruments. `detail` is caller-supplied and runs
    // DURING render, so a bad thunk (a typo, a value read before its declaration) would otherwise throw on
    // every render of an instrumented component — turning a debug aid into a dead page.
    let detailValue: Record<string, unknown> | string = '';
    try {
      detailValue = detail?.() ?? '';
    } catch (err) {
      detailValue = { detailError: err instanceof Error ? err.message : String(err) };
    }
    console.debug(`[seq] render ${name}#${idRef.current} ×${rendersRef.current}`, detailValue);
  }

  useEffect(() => {
    if (!on) return;
    const id = idRef.current;
    const stat = statFor(name);
    stat.live++;
    stat.everMounted++;
    console.debug(`[seq] + mount ${name}#${id} (live ${stat.live})`);
    return () => {
      stat.live--;
      console.debug(`[seq] − unmount ${name}#${id} (live ${stat.live})`);
    };
    // Effectively mount/unmount only: `name` is a string literal at every call site and `on` is fixed for
    // the page's lifetime, so this runs once per instance — but they ARE the effect's real inputs, so they
    // belong in the dep list rather than behind a suppression.
  }, [name, on]);
}

/**
 * Log a one-off graph event — an effect binding or releasing window listeners, a drag starting, a save
 * firing. Paired bind/release messages that stop pairing up are how you catch a listener leak.
 */
export function seqLog(event: string, detail?: Record<string, unknown>): void {
  if (!seqDebugEnabled()) return;
  ensureConsoleHook();
  console.debug(`[seq] ${event}`, detail ?? '');
}
